import React from "react";
import {
  Cpu,
  Code2,
  Workflow,
  Palette,
  ArrowUpRight,
  ShieldCheck,
  Bot,
} from "lucide-react";
import { TiltCard } from "./TiltCard";
import { RevealText } from "./RevealText";

const services = [
  {
    icon: Cpu,
    title: "AI & Automation",
    desc: "Automate workflows with Python, n8n, and intelligent agents that work 24/7.",
    tags: ["Python", "n8n", "LLM"],
  },
  {
    icon: Code2,
    title: "Web Development",
    desc: "Custom websites and landing pages crafted for speed, SEO, and conversion.",
    tags: ["React", "Next.js", "FastAPI"],
  },
  {
    icon: Workflow,
    title: "Efficiency Systems",
    desc: "Streamlined business processes that cut overhead and amplify output.",
    tags: ["Ops", "ETL", "CRM"],
  },
  {
    icon: Palette,
    title: "UI / UX Design",
    desc: "Human-centered digital experiences your users will love at first glance.",
    tags: ["Figma", "Design Systems"],
  },
  {
    icon: Bot,
    title: "Chatbot Engineering",
    desc: "Telegram, WhatsApp, and web bots tailored to capture and qualify leads.",
    tags: ["Telegram", "Webhook"],
  },
  {
    icon: ShieldCheck,
    title: "Reliable Maintenance",
    desc: "Proactive monitoring, performance tuning, and on-call engineering support.",
    tags: ["DevOps", "Monitoring"],
  },
];

export const Services = ({ onConsult }) => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-[#B026FF] mb-4">
              What We Build
            </p>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-white max-w-2xl">
              <RevealText>A full-stack tech studio</RevealText>{" "}
              <RevealText delay={180}>for ambitious teams.</RevealText>
            </h2>
          </div>
          <p className="text-white/60 max-w-md">
            From a polished landing page to a fleet of AI agents — we ship
            production-grade software, not slideware.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, idx) => {
            const Icon = s.icon;
            const featured = idx === 1; // make Web Development the featured one
            return (
              <TiltCard
                key={s.title}
                data-testid={`service-card-${idx}`}
                data-cursor="hover"
                className={`card-base relative rounded-2xl p-7 group cursor-pointer overflow-hidden ${
                  featured ? "lg:bg-[#1a0b2e]/60" : ""
                }`}
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#B026FF]/10 border border-[#B026FF]/30 text-[#B026FF] group-hover:bg-[#B026FF]/20 transition">
                    <Icon size={20} strokeWidth={1.8} />
                  </div>
                  <div className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-white/50 group-hover:text-white group-hover:border-[#B026FF]/60 transition">
                    <ArrowUpRight size={15} />
                  </div>
                </div>

                <h3 className="font-display text-xl font-semibold text-white mb-2">
                  {s.title}
                </h3>
                <p className="text-sm text-white/60 leading-relaxed">{s.desc}</p>

                <div className="mt-6 flex flex-wrap gap-2">
                  {s.tags.map((t) => (
                    <span
                      key={t}
                      className="text-[10px] font-mono-ui uppercase tracking-widest text-white/55 px-2.5 py-1 rounded-full border border-white/10 bg-white/[0.02]"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </TiltCard>
            );
          })}
        </div>

        <div className="mt-14 flex justify-center">
          <button
            data-testid="services-consult-btn"
            data-cursor="hover"
            onClick={onConsult}
            className="btn-ghost rounded-full px-6 h-11 text-sm"
          >
            Discuss your project →
          </button>
        </div>
      </div>
    </section>
  );
};
